import React, { useCallback, useContext } from 'react';
import EditableAttribute from '../../../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { getAttributeSafe } from '../../../../../../../../../Components/Urval/Utils/urvalUtils';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { ICollectionInfo } from '../../../../../../../../../Types/ClientModelTypes';
import { BusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  IKontodelDef,
  IKontodelTillaggsinfo,
} from '../../../../../../../_Common/Components/KonteringManager/Query/KonteringManagerQuery';
import { FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All } from '../../../../Queries/_QueryTypes/FakturaDelQuery';
import { useQuery } from '@apollo/client';
import TillaggsinfoQueryDoc from './Queries/TillaggsinfoQuery.gql';
import {
  TillaggsinfoQuery,
  TillaggsinfoQueryVariables,
} from './Queries/_QueryTypes/TillaggsinfoQuery';
import { ActiveDatabaseContext } from '../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IFieldValidations } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { TEDLRKonteringsForslagFI_REGISTRERASEnum } from '../../../../../../../../../globalQueryTypes';
import EDAvenyProgress from '../../../../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import ServerError from '../../../../../../../../../Components/ServerError/ServerError';
import ConsumableInventory from './Components/ConsumableInventory/ConsumableInventory';
import AdditionalInformation from './Components/AdditionalInformation/AdditionalInformation';
import { MutationOperation } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';

interface IProps {
  tillaggsinfo: IKontodelTillaggsinfo;
  originKonteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All | null;
  updatedKonteringsforslag: FakturaDelQuery_node_TEDLRFakturaDel__Associations_Faktura__Associations_Konteringsforslag_All;
  beloppValue: string;
  fakturaDelCollection: ICollectionInfo;
  konteringsforslagDisabled: boolean;
  onBeloppChange: (
    newValue: any,
    validationErrorMessage?: IFieldValidations
  ) => void;
  isBeloppChanged: boolean;
  isKomponent: boolean;
  kontodelsDef: IKontodelDef[];
  avskrivningKontodelOrdningsnummerList: number[];
  fakturaBokfDatum: Date;
  isForbrukning: boolean;
}

const Tillaggsinfo: React.FC<React.PropsWithChildren<IProps>> = props => {
  const modelContext = useContext(ModelContext);
  const dataForm = useContext(BusinessDataFormContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);

  const { data, loading, error } = useQuery<
    TillaggsinfoQuery,
    TillaggsinfoQueryVariables
  >(TillaggsinfoQueryDoc, {
    variables: {
      dbName: activeDatabaseContext.activeDatabase ?? '',
    },
    skip: !activeDatabaseContext.activeDatabase,
  });

  const isNewKonteringsforslag = props.originKonteringsforslag == null;

  const konteringsforslagUpdateId = isNewKonteringsforslag
    ? props.updatedKonteringsforslag.id
    : props.originKonteringsforslag?.id ?? '';

  const konteringsforslagUpdateOperation: MutationOperation =
    isNewKonteringsforslag
      ? MutationOperation.Add_Values
      : MutationOperation.Update_Values;

  const onFieldValueChange = useCallback(
    (fieldname: string) =>
      (
        newValue: any,
        validationErrorMessage?: IFieldValidations | undefined
      ) => {
        if (dataForm) {
          dataForm.update([
            {
              operation: 'OwnFields',
              values: { [fieldname]: newValue },
            },
          ]);
        }
      },
    [dataForm]
  );

  if (loading) {
    return <EDAvenyProgress />;
  } else if (error) {
    return <ServerError error={error} />;
  }

  const beloppAttribute = getAttributeSafe(
    'LRFakturaDel',
    'FakturaBelopp2',
    modelContext.avenyCollectionMap
  );

  const registerInventarium =
    (props.updatedKonteringsforslag?.FI_REGISTRERAS ??
      TEDLRKonteringsForslagFI_REGISTRERASEnum.EJ_ANGIVET) ===
    TEDLRKonteringsForslagFI_REGISTRERASEnum.JA;

  const showInventariumFields = registerInventarium && props.isForbrukning;

  return (
    <>
      {(showInventariumFields || !props.isForbrukning) && (
        <EditableAttribute
          collection={props.fakturaDelCollection.collection}
          attribute={beloppAttribute}
          value={props.beloppValue}
          label={
            props.isKomponent
              ? 'Komponentbelopp'
              : props.isForbrukning
              ? 'Belopp'
              : 'Investeringsbelopp'
          }
          editMode={true}
          disabled={
            !dataForm?.formState.editMode || props.konteringsforslagDisabled
          }
          onChange={props.onBeloppChange}
          marker={props.isBeloppChanged}
          size="large"
          required
        />
      )}
      {showInventariumFields && konteringsforslagUpdateId && (
        <ConsumableInventory
          data={data}
          konteringsforslagDisabled={props.konteringsforslagDisabled}
          konteringsforslagUpdateId={konteringsforslagUpdateId}
          konteringsforslagUpdateOperation={konteringsforslagUpdateOperation}
          onFieldValueChange={onFieldValueChange}
          originKonteringsforslag={props.originKonteringsforslag}
          tillaggsinfo={props.tillaggsinfo}
          updatedKonteringsforslag={props.updatedKonteringsforslag}
        />
      )}

      {!props.isForbrukning && konteringsforslagUpdateId && (
        <AdditionalInformation
          avskrivningKontodelOrdningsnummerList={
            props.avskrivningKontodelOrdningsnummerList
          }
          data={data}
          fakturaBokfDatum={props.fakturaBokfDatum}
          isKomponent={props.isKomponent}
          konteringsforslagDisabled={props.konteringsforslagDisabled}
          konteringsforslagUpdateId={konteringsforslagUpdateId}
          konteringsforslagUpdateOperation={konteringsforslagUpdateOperation}
          kontodelsDef={props.kontodelsDef}
          onFieldValueChange={onFieldValueChange}
          originKonteringsforslag={props.originKonteringsforslag}
          tillaggsinfo={props.tillaggsinfo}
          updatedKonteringsforslag={props.updatedKonteringsforslag}
        />
      )}
    </>
  );
};

export default Tillaggsinfo;
